import { useEffect, useCallback } from "react";
import { useBlocker } from "react-router-dom";
import { useConfirm } from "./useConfirm";

const Prompt = ({
  isDirty = false,
  title = "You have unsaved changes!",
  subtitle = "Are you sure you want to leave?",
  confirmText = "Leave",
  cancelText = "Stay",
  onConfirm,
  onCancel,
  type = "warning",
}) => {
  const blocker = useBlocker(isDirty); // Block the navigation if the page has unsaved changes
  const { show } = useConfirm(); // Custom hook to display the confirmation modal

  const confirm = useCallback(() => {
    if (!isDirty) return Promise.resolve(true); // If there's no unsaved change, allow navigation

    // Display confirmation dialog and return a promise that resolves based on user action
    return new Promise((resolve) => {
      show({
        title,
        subtitle,
        confirmText,
        cancelText,
        type,
        onConfirm: () => {
          resolve(true);
          onConfirm?.(); // Call onConfirm callback
        },
        onCancel: () => {
          resolve(false);
          onCancel?.(); // Call onCancel callback
        },
      });
    });
  }, [
    cancelText,
    confirmText,
    isDirty,
    onCancel,
    onConfirm,
    show,
    subtitle,
    title,
    type,
  ]);

  // Handling blocked navigation logic
  useEffect(() => {
    if (blocker.state === "blocked") {
      confirm().then((result) => {
        if (result) blocker.proceed(); // Proceed with navigation if confirmed
        else blocker.reset(); // Otherwise, reset the block
      });
    }
  }, [blocker, confirm]);

  // Handle the browser's before unload event for unsaved changes
  useEffect(() => {
    if (isDirty) {
      window.onbeforeunload = () => subtitle; // Display subtitle on page close
    }
    return () => {
      window.onbeforeunload = null; // Clean up the unload event
    };
  }, [isDirty, subtitle]);

  return null;
};

export default Prompt;
